import React, { useCallback, useState, useEffect } from 'react';
import { SimpleGrid, Stack, MultiSelect, Space, Text, Flex, Checkbox, Container } from '@mantine/core';
import { useLoaderData } from 'react-router-dom';
import Input from '../../Components/Input';
import TechnologyCard from './Card';
import { IconAffiliate, IconPlaystationCircle, IconRadar, IconTag } from '@tabler/icons';
import { AggregatedDataLoaderResult } from '../../Loaders/AggregatedDataLoader';
import useUserData from '../../Hooks/use-user-data';
import { Technology } from '../../../backend/models/technology';
import { BaseLoaderResult } from '../../Loaders/BaseLoader';

const filterParams: {
  [K in keyof Omit<BaseLoaderResult, 'persons'>]: K;
} = {
  sections: 'sections',
  rings: 'rings',
  tags: 'tags',
  clients: 'clients',
};

function technologyIsVisibleInRadar(technology: Technology, sectionNames: Array<string>, ringNames: Array<string>) {
  return (
    technology.ring &&
    technology.section &&
    ringNames.includes(technology.ring.name) &&
    sectionNames.includes(technology.section.name)
  );
}

const TechnologyList = () => {
  const { technologies, rings, sections, tags, clients } = useLoaderData() as AggregatedDataLoaderResult;
  const { isAdministrator } = useUserData();

  const sectionNames = sections.map((s) => s.name);
  const ringNames = rings.map((r) => r.name);
  const hasInvalids = technologies.some((t) => !technologyIsVisibleInRadar(t, sectionNames, ringNames));

  const parseFilterParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      sections: params.get(filterParams.sections)?.split(',').filter(Boolean) || [],
      rings: params.get(filterParams.rings)?.split(',').filter(Boolean) || [],
      tags: params.get(filterParams.tags)?.split(',').filter(Boolean) || [],
      clients: params.get(filterParams.clients)?.split(',').filter(Boolean) || [],
    };
  }, []);

  const filterUrlParams = parseFilterParams();

  const [filteredSections, setFilteredSections] = useState<string[]>(() => filterUrlParams.sections);
  const [filteredRings, setFilteredRings] = useState<string[]>(() => filterUrlParams.rings);
  const [filteredTags, setFilteredTags] = useState<string[]>(() => filterUrlParams.tags);
  const [filteredClients, setFilteredClients] = useState<string[]>(() => filterUrlParams.clients);
  const [showInvalidsOnly, setShowInvalidsOnly] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (filteredSections.length > 0) params.set(filterParams.sections, filteredSections.join(','));
    if (filteredRings.length > 0) params.set(filterParams.rings, filteredRings.join(','));
    if (filteredTags.length > 0) params.set(filterParams.tags, filteredTags.join(','));
    if (filteredClients.length > 0) params.set(filterParams.clients, filteredClients.join(','));

    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    if (newUrl !== window.location.href) {
      window.history.replaceState(null, '', newUrl);
    }
  }, [filteredSections, filteredRings, filteredTags, filteredClients]);

  const filteredTechnologies = technologies.filter((t) => {
    const visibleInRadar = technologyIsVisibleInRadar(t, sectionNames, ringNames);
    if (!isAdministrator && !visibleInRadar) {
      return false;
    }
    if (showInvalidsOnly && visibleInRadar) {
      return false;
    }
    if (filteredSections.length > 0 && (!t.section || !filteredSections.includes(t.section.name))) {
      return false;
    }
    if (filteredRings.length > 0 && (!t.ring || !filteredRings.includes(t.ring.name))) {
      return false;
    }
    if (filteredTags.length > 0 && !t.tags?.some((t) => filteredTags.includes(t.id))) {
      return false;
    }
    if (filteredClients.length > 0 && !t.clients?.some((t) => filteredClients.includes(t.id))) {
      return false;
    }
    if (searchTerm) {
      return t.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());
    }
    return true;
  });

  return (
    <Container size={'xl'}>
      <Stack maw={900}>
        <Input
          label="Technology name"
          placeholder="Filter by technology name"
          value={searchTerm}
          onChange={(value) => setSearchTerm(value.currentTarget.value)}
          required={false}
          maw={400}
        />
        <SimpleGrid
          breakpoints={[
            { minWidth: 'md', cols: 4, spacing: 'md' },
            { minWidth: 'xs', cols: 1, spacing: 'sm' },
          ]}
          style={{ alignItems: 'flex-start' }}
        >
          <MultiSelect
            data={sectionNames}
            label={
              <Flex align={'center'}>
                <IconRadar size={16} />
                <Text ml={4}>{'Section(s)'}</Text>
              </Flex>
            }
            placeholder="Filter by section"
            value={filteredSections}
            onChange={setFilteredSections}
            maw={400}
          />
          <MultiSelect
            data={ringNames}
            label={
              <Flex align={'center'}>
                <IconPlaystationCircle size={16} />
                <Text ml={4}>{'Ring(s)'}</Text>
              </Flex>
            }
            placeholder="Filter by rings"
            value={filteredRings}
            onChange={setFilteredRings}
            maw={400}
          />
          <MultiSelect
            data={tags.map((t) => {
              return { value: t.id, label: t.name };
            })}
            label={
              <Flex align={'center'}>
                <IconTag size={16} />
                <Text ml={4}>{'Tag(s)'}</Text>
              </Flex>
            }
            placeholder="Filter by tags"
            value={filteredTags}
            onChange={setFilteredTags}
            maw={400}
            searchable
          />
          <MultiSelect
            data={clients.map((c) => {
              return { value: c.id, label: c.name };
            })}
            label={
              <Flex align={'center'}>
                <IconAffiliate size={16} />
                <Text ml={4}>{'Client(s)'}</Text>
              </Flex>
            }
            placeholder="Filter by clients"
            value={filteredClients}
            onChange={setFilteredClients}
            maw={400}
            searchable
          />
          {isAdministrator && hasInvalids && (
            <Checkbox
              checked={showInvalidsOnly}
              onChange={(event) => setShowInvalidsOnly(event.currentTarget.checked)}
              label={<Text fw={500}>Show only invalids</Text>}
            />
          )}
        </SimpleGrid>
      </Stack>
      <Space h="md" />
      <Text color="dimmed" size={'sm'}>{`${filteredTechnologies.length} technologies`}</Text>
      <Space h="md" />
      <SimpleGrid
        breakpoints={[
          { minWidth: 'md', cols: 3, spacing: 'md' },
          { minWidth: 'sm', cols: 2, spacing: 'sm' },
          { minWidth: 'xs', cols: 1, spacing: 'sm' },
        ]}
      >
        {filteredTechnologies.map((technology) => (
          <TechnologyCard
            key={technology.name}
            technology={technology}
            invalidRingName={!technology.ring || !ringNames.includes(technology.ring.name)}
            invalidSectionName={!technology.section || !sectionNames.includes(technology.section.name)}
          />
        ))}
      </SimpleGrid>
    </Container>
  );
};

export default TechnologyList;
